import { UrlBuilder } from './../../core/url-builder/url-builder';
import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import {
    Category, CategoryNaturalKey, DocumentsColumns, downloadColumn,
    isResponseRequiredColumn, letterNumberColumn, postedColumn, regionColumn, responseNeededByColumn, selectColumn,
    sitesColumn, titleColumn, versionDateColumn, versionNumberColumn, visitColumn, zcProjectNumberColumn, getSiteNumColumn, LabReport, viewColumn, categoryColumn, downloadViewColumn, reportTypeColumn, visitCollectionDateColumn, VisitReportRendererParams
} from '../../documents';
import { GroupingOptionItem } from '../../grouping-selector';
import { SponsorRouteData } from '../sponsor-route-data';
import { GridColumns } from '../../shared/grid-columns';

@Injectable()
export class SponsorDocumentsColumns implements DocumentsColumns {
    private readonly subjectColumn: ColDef;
    private readonly accessionNumberColumn: ColDef;
    private readonly columnsDictionary: Map<CategoryNaturalKey, ColDef[]>;

    constructor(urlBuilder: UrlBuilder, sponsorRouteData: SponsorRouteData, gridColumns: GridColumns) {
        const subjectLinkBuilder = (params: {data: LabReport}) => {
            const selectedStudy = sponsorRouteData.snapshot.selectedStudy;

            return urlBuilder
                .esa()
                .sponsor()
                .study(selectedStudy)
                .subjects(params.data.zcSubjectId)
                .url();
        };

        const testResultsLinkBuilder = (params: VisitReportRendererParams) => {
            const selectedStudy = sponsorRouteData.snapshot.selectedStudy;
            return urlBuilder
            .esa()
            .sponsor()
            .study(selectedStudy)
            .testResults(params.data.siteId)
            .standardTestResults()
            .url();
        };

        this.subjectColumn = gridColumns.subjectColumn(subjectLinkBuilder);
        this.accessionNumberColumn = gridColumns.accessionNumberColumn(testResultsLinkBuilder, data => data.accessionNumbers);
        this.columnsDictionary = this.buildColumnsDictionary();
    }

    public getColumnDefinitions(category: Category | undefined): ColDef[] {
        if (!category) {
            return [];
        }

        return this.columnsDictionary.get(category.categoryId) || [];
    }

    public hasStatusColumn(category: CategoryNaturalKey) {
        return false;
    }

    public getGroupedColumns(category: string): GroupingOptionItem[] {
        return [];
    }

    private buildColumnsDictionary(): Map<CategoryNaturalKey, ColDef[]> {
        const sponsorCommunicationsColumns = [
            selectColumn,
            categoryColumn,
            titleColumn,
            letterNumberColumn,
            postedColumn,
            downloadViewColumn
        ];

        const studyManualsColumns = [
            selectColumn,
            zcProjectNumberColumn,
            versionNumberColumn,
            postedColumn,
            regionColumn,
            downloadColumn
        ];

        const synopsisColumns = [
            selectColumn,
            zcProjectNumberColumn,
            versionDateColumn,
            postedColumn,
            regionColumn,
            downloadColumn
        ];

        const labManualsOthersColumns = [
            selectColumn,
            zcProjectNumberColumn,
            titleColumn,
            postedColumn,
            regionColumn,
            downloadColumn
        ];

        const trainingMaterialsOthersColumns = [
            selectColumn,
            titleColumn,
            postedColumn,
            downloadColumn
        ];

        const trainingMaterialsELearningColumns = [
            titleColumn,
            postedColumn,
            viewColumn
        ];

        const siteCommunicationsColumns = [
            selectColumn,
            titleColumn,
            letterNumberColumn,
            sitesColumn,
            postedColumn,
            downloadColumn
        ];

        const siteActionsColumns = [
            selectColumn,
            titleColumn,
            this.subjectColumn,
            this.accessionNumberColumn,
            visitColumn,
            isResponseRequiredColumn,
            responseNeededByColumn,
            getSiteNumColumn(),
            postedColumn,
            downloadColumn
        ];

        const resultAlertsColumns = [
            selectColumn,
            this.subjectColumn,
            this.accessionNumberColumn,
            visitColumn,
            visitCollectionDateColumn,
            getSiteNumColumn(),
            postedColumn,
            downloadColumn
        ];

        const labReportsColumns = [
            selectColumn,
            reportTypeColumn,
            this.subjectColumn,
            this.accessionNumberColumn,
            visitColumn,
            visitCollectionDateColumn,
            getSiteNumColumn(),
            postedColumn,
            downloadColumn
        ];

        const map = new Map<CategoryNaturalKey, ColDef[]>();
        map.set('sponsor-communications', sponsorCommunicationsColumns);
        map.set('study-manuals', studyManualsColumns);
        map.set('synopsis', synopsisColumns);
        map.set('lab-manuals-others', labManualsOthersColumns);
        map.set('training-materials-others', trainingMaterialsOthersColumns);
        map.set('training-materials-e-learning', trainingMaterialsELearningColumns);
        map.set('site-communications', siteCommunicationsColumns);
        map.set('site-actions', siteActionsColumns);
        map.set('result-alerts', resultAlertsColumns);
        map.set('lab-reports', labReportsColumns);

        return map;
    }
}
